import { useState, useEffect, useRef } from 'react';
import { reportCloudinaryFailure } from '../utils/helpers';

// Pokeball spinning fallback - SVG animated
const PokeballSpinner = ({ size = 80 }) => (
  <div 
    className="flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <svg 
      viewBox="0 0 100 100" 
      className="animate-spin"
      style={{ 
        width: size * 0.8, 
        height: size * 0.8,
        animationDuration: '1.5s'
      }}
    >
      {/* Top half - red */}
      <path 
        d="M 50 5 A 45 45 0 0 1 95 50 L 65 50 A 15 15 0 0 0 35 50 L 5 50 A 45 45 0 0 1 50 5" 
        fill="#EF4444"
        stroke="#1F2937"
        strokeWidth="3"
      />
      {/* Bottom half - white */}
      <path 
        d="M 50 95 A 45 45 0 0 1 5 50 L 35 50 A 15 15 0 0 0 65 50 L 95 50 A 45 45 0 0 1 50 95" 
        fill="#F8FAFC"
        stroke="#1F2937"
        strokeWidth="3"
      />
      {/* Center line */}
      <line x1="5" y1="50" x2="35" y2="50" stroke="#1F2937" strokeWidth="3" />
      <line x1="65" y1="50" x2="95" y2="50" stroke="#1F2937" strokeWidth="3" />
      {/* Center button */}
      <circle cx="50" cy="50" r="12" fill="#F8FAFC" stroke="#1F2937" strokeWidth="3" />
      <circle cx="50" cy="50" r="6" fill="#1F2937" />
    </svg>
  </div>
);

const LOAD_TIMEOUT = 8000; // 8 seconds timeout for fallback (GIFs can be large)

const PokemonImage = ({ 
  src, 
  alt = "Pokémon", 
  className = "w-20 h-20 object-contain",
  fallbackSize = 80,
  fallbackSrc = null,  // optional public URL used if `src` fails (e.g., Cloudinary down)
  onLoad,
  onError 
}) => {
  const [imageStatus, setImageStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [currentSrc, setCurrentSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);
  const [usedFallback, setUsedFallback] = useState(false);
  const timeoutRef = useRef(null);
  const imgRef = useRef(null);
  const MAX_RETRIES = 3;

  // Reset status when src changes
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setImageStatus('loading');
    setRetryCount(0);
    setUsedFallback(false);
    
    // Cache-busting only for backend-served files (forces refresh after
    // teacher re-upload). CDN URLs (ImageKit/Cloudinary/GitHub) are immutable
    // and must stay clean so the browser/CDN cache works at full speed.
    const isCDN = src && (src.includes('cloudinary.com') || src.includes('ik.imagekit.io') || src.includes('raw.githubusercontent.com'));
    const cacheBuster = isCDN ? '' : `?v=${Date.now()}`;
    setCurrentSrc(src ? `${src}${cacheBuster}` : src);
    
    // Set timeout for fallback
    timeoutRef.current = setTimeout(() => {
      if (imageStatus === 'loading') {
        console.warn(`[PokemonImage] Timeout loading: ${src}`);
        handleRetry();
      }
    }, LOAD_TIMEOUT);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [src]);

  const handleRetry = () => {
    // First: try the public fallback URL once before counting retries.
    // This recovers automatically when Cloudinary is disabled/exhausted.
    if (!usedFallback && fallbackSrc && fallbackSrc !== src) {
      console.warn(`[PokemonImage] Primary failed, swapping to fallbackSrc: ${fallbackSrc}`);
      setUsedFallback(true);
      setCurrentSrc(fallbackSrc);
      setImageStatus('loading');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (imageStatus === 'loading') {
          handleRetry();
        }
      }, LOAD_TIMEOUT);
      return;
    }

    if (retryCount < MAX_RETRIES) {
      console.log(`[PokemonImage] Retrying (${retryCount + 1}/${MAX_RETRIES}): ${currentSrc}`);
      setRetryCount(prev => prev + 1);
      // Add cache buster to force reload
      const baseSrc = usedFallback ? fallbackSrc : src;
      const newSrc = baseSrc ? `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}retry=${Date.now()}` : baseSrc;
      setCurrentSrc(newSrc);
      setImageStatus('loading');
      
      // Reset timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (imageStatus === 'loading') {
          handleRetry();
        }
      }, LOAD_TIMEOUT);
    } else {
      setImageStatus('error');
      onError?.({ type: 'max_retries', src });
    }
  };

  const handleLoad = (e) => {
    // Clear timeout on successful load
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setImageStatus('loaded');
    onLoad?.(e);
  };

  const handleError = (e) => {
    // Clear timeout on error
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    console.warn(`[PokemonImage] Failed to load: ${currentSrc}`);
    // Tell the global helper so other instances stop using Cloudinary too
    reportCloudinaryFailure(currentSrc);
    handleRetry();
  };

  // Show pokeball spinner while loading or on error
  if (imageStatus === 'error' || !src) {
    return <PokeballSpinner size={fallbackSize} />;
  }

  return (
    <div className="relative" style={{ minWidth: fallbackSize, minHeight: fallbackSize }}>
      {/* Loading spinner (shown while loading) */}
      {imageStatus === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <PokeballSpinner size={fallbackSize} />
        </div>
      )}
      
      {/* Actual image */}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        loading="lazy"
        className={`${className} ${imageStatus === 'loading' ? 'opacity-0' : 'opacity-100'}`}
        style={{ transition: 'opacity 0.3s ease-in-out' }}
        onLoad={handleLoad}
        onError={handleError}
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default PokemonImage;
export { PokeballSpinner };
